import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Eye, EyeOff, LibraryBig, ShieldOff, Shield, Users } from "lucide-react";
import { PageHeader, StatusPill } from "../components/Primitives";
import { Modal } from "../components/Modal";
import { api, errorText, inr, formatDate } from "../lib/api";

const emptyForm = {
  name: "",
  owner_name: "",
  email: "",
  mobile: "",
  address: "",
  city: "",
  state: "",
  pincode: "",
  total_seats: 50,
  opening_time: "06:00",
  closing_time: "22:00",
  plan_id: "",
  notes: "",
  admin_password: "",
  send_welcome_email: true,
};

export default function Libraries() {
  const navigate = useNavigate();
  const [libraries, setLibraries] = useState([]);
  const [plans, setPlans] = useState([]);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("ALL");
  const [error, setError] = useState("");
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [showPassword, setShowPassword] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [librariesRes, plansRes] = await Promise.all([api.get("/libraries"), api.get("/plans")]);
      setLibraries(librariesRes.data.data);
      setPlans(plansRes.data.data);
    } catch (err) {
      setError(errorText(err));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    return libraries.filter((library) => {
      if (filter !== "ALL" && library.status !== filter) return false;
      if (!query) return true;
      const needle = query.toLowerCase();
      return (
        library.name.toLowerCase().includes(needle) ||
        library.owner_name?.toLowerCase().includes(needle) ||
        library.mobile?.toLowerCase().includes(needle) ||
        library.email?.toLowerCase().includes(needle)
      );
    });
  }, [libraries, filter, query]);

  const closeForm = () => {
    setShow(false);
    setForm(emptyForm);
    setShowPassword(false);
    setError("");
  };

  const create = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...form,
        total_seats: Number(form.total_seats),
        admin_password: form.admin_password.trim() || undefined,
      };
      if (!payload.plan_id) delete payload.plan_id;
      if (!payload.admin_password) delete payload.admin_password;
      const response = await api.post("/libraries", payload);
      closeForm();
      if (response.data.credentials) {
        setCredentials(response.data.credentials);
      }
      load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (library, status) => {
    try {
      await api.patch(`/libraries/${library.id}/status`, { status });
      load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  return (
    <div className="page-enter">
      <PageHeader
        eyebrow="TENANT MANAGEMENT"
        title="Libraries"
        description="Create new tenants, assign plans and keep each library's operations isolated."
        action={
          <button className="button button-primary" onClick={() => setShow(true)} data-testid="add-library-button">
            <LibraryBig size={16} /> Add library
          </button>
        }
      />
      {error && !show ? <div className="error-message" data-testid="libraries-error">{error}</div> : null}

      <section className="content-card table-card">
        <div className="table-toolbar">
          <div>
            <span className="card-kicker">TENANTS</span>
            <h2>{filtered.length} libraries</h2>
          </div>
          <div className="toolbar-actions">
            <div className="table-search">
              <Users size={17} />
              <input
                placeholder="Search by name, owner, mobile…"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                data-testid="libraries-search"
              />
            </div>
            <select value={filter} onChange={(event) => setFilter(event.target.value)} data-testid="libraries-filter">
              <option value="ALL">All statuses</option>
              <option value="ACTIVE">Active</option>
              <option value="BLOCKED">Blocked</option>
              <option value="EXPIRED">Expired</option>
            </select>
          </div>
        </div>
        <div className="table-wrap">
          <table data-testid="libraries-table">
            <thead>
              <tr>
                <th>Library</th>
                <th>Owner</th>
                <th>Students</th>
                <th>Seats</th>
                <th>Plan</th>
                <th>Expiry</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((library) => (
                <tr key={library.id} data-testid={`library-row-${library.id}`} onClick={() => navigate(`/libraries/${library.id}`)} style={{ cursor: "pointer" }}>
                  <td>
                    <div className="person-cell">
                      <span className="table-avatar">{library.name.charAt(0)}</span>
                      <div>
                        <strong>{library.name}</strong>
                        <small>{library.city} · {library.email}</small>
                      </div>
                    </div>
                  </td>
                  <td>{library.owner_name}<br /><small style={{ color: "#64748b" }}>{library.mobile}</small></td>
                  <td>{library.students}</td>
                  <td>{library.occupied_seats}/{library.seats || library.total_seats}</td>
                  <td>{library.plan}</td>
                  <td>{formatDate(library.subscription_end)}</td>
                  <td><StatusPill status={library.status} /></td>
                  <td>
                    <div className="action-row" onClick={(event) => event.stopPropagation()}>
                      {library.status === "ACTIVE" ? (
                        <button className="icon-button" title="Block" onClick={() => changeStatus(library, "BLOCKED")} data-testid={`block-library-${library.id}`}>
                          <ShieldOff size={16} />
                        </button>
                      ) : (
                        <button className="icon-button" title="Activate" onClick={() => changeStatus(library, "ACTIVE")} data-testid={`activate-library-${library.id}`}>
                          <Shield size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {show ? (
        <Modal title="Create library" eyebrow="NEW TENANT" close={closeForm} testId="add-library-modal" wide>
          <form className="student-form" onSubmit={create}>
            {error ? <div className="error-message" data-testid="add-library-error">{error}</div> : null}

            <div className="form-section">
              <div className="form-section-head"><span className="hero-label">LIBRARY</span><h3>Basic details</h3></div>
              <div className="form-grid">
                <label>Library name<input required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} data-testid="library-name-input" placeholder="North Block Study Hall" /></label>
                <label>City<input required value={form.city} onChange={(event) => setForm({ ...form, city: event.target.value })} data-testid="library-city-input" /></label>
                <label>State<input value={form.state} onChange={(event) => setForm({ ...form, state: event.target.value })} /></label>
                <label>Pincode<input value={form.pincode} onChange={(event) => setForm({ ...form, pincode: event.target.value })} /></label>
                <label className="field-full">Address<input value={form.address} onChange={(event) => setForm({ ...form, address: event.target.value })} /></label>
                <label>Total seats<input type="number" min="1" required value={form.total_seats} onChange={(event) => setForm({ ...form, total_seats: event.target.value })} data-testid="library-seats-input" /></label>
                <label>Opening time<input type="time" value={form.opening_time} onChange={(event) => setForm({ ...form, opening_time: event.target.value })} /></label>
                <label>Closing time<input type="time" value={form.closing_time} onChange={(event) => setForm({ ...form, closing_time: event.target.value })} /></label>
              </div>
            </div>

            <div className="form-section">
              <div className="form-section-head"><span className="hero-label">OWNER</span><h3>Primary library admin</h3></div>
              <div className="form-grid">
                <label>Owner name<input required value={form.owner_name} onChange={(event) => setForm({ ...form, owner_name: event.target.value })} data-testid="library-owner-input" /></label>
                <label>Owner email<input type="email" required value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} data-testid="library-email-input" placeholder="owner@library.com" /></label>
                <label>Mobile<input required value={form.mobile} onChange={(event) => setForm({ ...form, mobile: event.target.value })} data-testid="library-mobile-input" placeholder="+91 90000 00000" /></label>
                <label className="field-full">
                  Admin password <small style={{ color: "#64748b", fontWeight: 500 }}>· leave blank to auto-generate a secure one</small>
                  <div className="password-field">
                    <input
                      type={showPassword ? "text" : "password"}
                      minLength={form.admin_password ? 8 : undefined}
                      value={form.admin_password}
                      onChange={(event) => setForm({ ...form, admin_password: event.target.value })}
                      data-testid="library-password-input"
                      placeholder="Minimum 8 characters"
                      autoComplete="new-password"
                    />
                    <button type="button" className="icon-button" onClick={() => setShowPassword((previous) => !previous)} aria-label={showPassword ? "Hide password" : "Show password"} data-testid="library-password-toggle">
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </label>
                <label className="field-full inline-check">
                  <input type="checkbox" checked={form.send_welcome_email} onChange={(event) => setForm({ ...form, send_welcome_email: event.target.checked })} data-testid="library-welcome-email-toggle" />
                  <span>Email the owner their sign-in details</span>
                </label>
              </div>
            </div>

            <div className="form-section">
              <div className="form-section-head"><span className="hero-label">PLAN</span><h3>Subscription</h3></div>
              <div className="form-grid">
                <label className="field-full">Plan
                  <select value={form.plan_id} onChange={(event) => setForm({ ...form, plan_id: event.target.value })} data-testid="library-plan-select">
                    <option value="">Default (Trial · 14 days)</option>
                    {plans.map((plan) => (
                      <option key={plan.id} value={plan.id}>{plan.name} · {inr(plan.price)} · {plan.duration_days}d</option>
                    ))}
                  </select>
                </label>
                <label className="field-full">Internal notes<input value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} placeholder="Reseller, referral code, special terms…" /></label>
              </div>
            </div>

            <div className="modal-actions sticky-actions">
              <button type="button" className="button button-outline" onClick={closeForm}>Cancel</button>
              <button className="button button-primary" type="submit" disabled={saving} data-testid="save-library-button">
                {saving ? "Creating…" : "Create library"} <ArrowRight size={16} />
              </button>
            </div>
          </form>
        </Modal>
      ) : null}

      {credentials ? (
        <Modal title="Owner credentials ready" eyebrow="LIBRARY CREATED" close={() => setCredentials(null)} testId="credentials-modal">
          <p style={{ color: "#64748b", marginBottom: 10 }}>
            {credentials.password_set_by_admin ? "Owner can sign in with the password you set." : "We generated a secure password for the owner."}
            {credentials.welcome_email_sent ? " A welcome email was sent." : " Share these details manually — no email was sent."}
          </p>
          <div className="receipt" data-testid="credentials-summary">
            <div className="receipt-row"><span>Email</span><strong>{credentials.email}</strong></div>
            <div className="receipt-row"><span>Password</span><strong>{credentials.temporary_password}</strong></div>
          </div>
          <div className="modal-actions">
            <button type="button" className="button button-outline" onClick={() => navigator.clipboard?.writeText(`${credentials.email} / ${credentials.temporary_password}`)} data-testid="credentials-copy-button">Copy</button>
            <button type="button" className="button button-primary" onClick={() => setCredentials(null)} data-testid="credentials-close-button">Done</button>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
